/**
 * Records service — read, create, update, delete. `update` runs every change
 * through `planRecordWrite`, so relation-typed keys become stamped junction rows
 * and only genuine column/value keys reach the record row and its `data`.
 *
 * Every method returns plain rows, typed as the document it runs selects them.
 * A caller that wants a record's resolved fields passes the row to
 * `enrichRecord`.
 */

import type { ResultOf } from "@graphql-typed-document-node/core"
import {
  enrichRecord,
  ownerWrite,
  planRecordWrite,
  RECORD_COLUMNS,
  RECORD_FIXED_KEYS,
  type RecordItem,
  type RecordWritePlan,
} from "../collections/records.js"
import { type AugerField, isRelationField } from "../fields/fields.js"
import { hasActiveConditions, type SavedFilter } from "../fields/filter.js"
import {
  archiveRecordsConditional,
  createRecordNested,
  createRecordsNested,
  destroyRecord,
  fetchFilterMatchIds,
  fetchRecordById,
  fetchRecordBySlug,
  fetchRecordLeanById,
  fetchRecordsArchivedState,
  fetchRecordsByEmails,
  fetchRecordsByIds,
  fetchRecordsLeanByIds,
  mergeRecords,
  searchAnyRecords,
  searchFilteredRecords,
  searchRecords,
  unarchiveRecordsConditional,
  updateRecordById,
  updateRecordsMany,
} from "../gql/records.js"
import type { Mutate, Query } from "../index.js"
import {
  addWritten,
  applyRelationOps,
  buildRelationInserts,
  noRowsWritten,
  prepareRelationOps,
  type Relation,
  relationOf,
  resolveAssetTargets,
  type WrittenRows,
  type WrittenRowTypes,
} from "./relations.js"
import { createSpans, LISTING, spanRow, trackedValue, trackingFields } from "./spans.js"

/** A record's columns (RecordFields) — what a write touching no relation returns. */
export type RecordRow = NonNullable<ResultOf<typeof updateRecordById>["update_records_by_pk"]>

/** A record as `get` and `getBySlug` return it (FullRecordFields): its columns and
 *  every owned-row relation. */
export type FullRecord = NonNullable<ResultOf<typeof fetchRecordById>["record"]>

/** Each relation's current rows on a full record. Its selection includes the
 *  fragment the relation's insert returns, so the rows have the same type. */
const CURRENT_ROWS: { [R in Relation]: (record: FullRecord) => WrittenRowTypes[R][] } = {
  links: (record) => record.links,
  times: (record) => record.times,
  contacts: (record) => record.contacts,
  taggings: (record) => record.taggings,
  attachments: (record) => record.attachments,
  sources: (record) => record.sources,
}

function currentRows<R extends Relation>(record: FullRecord, relation: R): WrittenRowTypes[R][] {
  const read: (record: FullRecord) => WrittenRowTypes[R][] = CURRENT_ROWS[relation]
  return read(record)
}

/** A record as `list` returns it: columns, taggings, contacts, events and
 *  organizations. */
export type ListedRecord = ResultOf<typeof fetchRecordsByIds>["records"][number]

/** A record as `search` returns it: columns, sources, listings (with their list)
 *  and taggings. */
export type SearchedRecord = ResultOf<typeof searchRecords>["records"][number]

/** A record found by one of its email addresses. */
export type EmailMatch = NonNullable<
  ResultOf<typeof fetchRecordsByEmails>["record_contacts"][number]["record"]
>

/** A record as `create` and `createMany` return it: columns and the owned-row
 *  relations its insert wrote. */
export type CreatedRecord = NonNullable<ResultOf<typeof createRecordNested>["insert_records_one"]>

export interface CreateRecordInput {
  type: string
  name: string
  /**
   * Omit to have the database generate a collision-safe one — the key is then
   * left out of the insert object entirely, since `records.slug` is NOT NULL
   * and Hasura rejects an explicit null for it before the trigger can run. A
   * `BEFORE INSERT` trigger (set_default_record_slug) fills `records.slug` from
   * `generate_unique_slug(name, type)` when the insert leaves it NULL — the
   * same collision-safe generator the Pipedrive/Airtable/Luma sync scripts
   * already call directly. Since the fill happens inside the INSERT
   * statement itself, the ask-then-use window shrinks from a network round
   * trip to microseconds — narrowed, not closed: two concurrent creates of
   * the same name can still both compute the same slug, and the loser gets
   * the (type, slug) unique violation, loudly. `records.slug` is NOT NULL
   * with no default; an explicit caller-supplied slug is trimmed, stored
   * otherwise verbatim, and can still collide (an empty or whitespace-only
   * slug is treated as omitted).
   */
  slug?: string | null
  description?: string | null
  parent_id?: number | null
  schema_id?: number | null
  data?: Record<string, unknown> | null
  visibility?: string | null
  /** Lists the new record joins, inserted in the same transaction as the record.
   *  Membership rows carry no context or position (they sort with the list's
   *  unpositioned rows); use `listings.add` for either. */
  listIds?: number[] | null
}

/** `updateMany` refuses a relation field: its value is rows per record, which
 *  one write can't reconcile. Write each record with `update` instead. */
export class RelationFieldInBatchError extends Error {
  constructor(readonly key: string) {
    super(
      `updateMany: "${key}" is a relation field — its value is rows per record, ` +
        `which one write can't reconcile. Use records.update for each record.`
    )
    this.name = "RelationFieldInBatchError"
  }
}

export interface UpdateManyResult {
  /** The written rows, in database order. */
  updated: RecordRow[]
  /** Requested ids no record was found for (never existed, or not visible). */
  missingIds: number[]
}

export interface DestroyManyResult {
  destroyedIds: number[]
  /** Ids no record was found for (never existed, already gone, or not visible). */
  missingIds: number[]
  /** Ids whose destroy failed outright, with the reason. */
  failures: Array<{ id: number; message: string }>
}

export interface ArchiveManyResult {
  archived: RecordRow[]
  alreadyArchivedIds: number[]
  missingIds: number[]
}

export interface UnarchiveManyResult {
  unarchived: RecordRow[]
  alreadyUnarchivedIds: number[]
  missingIds: number[]
}

/** records.slug's column width. */
export const SLUG_MAX_LENGTH = 64

/** How far up a parent chain the cycle check walks before refusing. */
const MAX_PARENT_DEPTH = 64

export function createRecordsService({ query, mutate }: { query: Query; mutate: Mutate }) {
  const spans = createSpans({ query, mutate })

  // Stamp a span for each tracked value this write changes on `before`.
  const stampChanges = async (
    id: number,
    fields: readonly AugerField[],
    before: { data?: unknown; [k: string]: unknown },
    changes: Record<string, unknown>,
    at: string
  ) => {
    for (const [key, to] of Object.entries(changes)) {
      if (to === undefined || key === LISTING) continue
      const tracking = trackingFields(fields, key)
      const from = trackedValue(before, key)
      if (tracking.length && from !== to) await spans.stamp(id, tracking, { from, to }, at)
    }
  }

  // The open spans a new record starts with: one per tracked value it is
  // created holding, and one per list it joins when a field tracks `listing`.
  const openingSpans = async (
    fields: readonly AugerField[],
    input: CreateRecordInput,
    data: Record<string, unknown>
  ) => {
    const at = new Date().toISOString()
    const rows = []
    for (const field of fields) {
      if (
        !field.tracks ||
        field.tracks === LISTING ||
        !trackingFields([field], field.tracks).length
      )
        continue
      const to = trackedValue({ ...input, data }, field.tracks)
      if (to != null) rows.push(spanRow(field, null, { from: null, to }, at))
    }
    const listing = trackingFields(fields, LISTING)
    if (listing.length && input.listIds?.length) {
      const lists = (await query(fetchRecordsLeanByIds, { ids: input.listIds })).records
      const names = new Map(lists.map((l) => [l.id, l.name]))
      for (const field of listing) {
        for (const listId of input.listIds) {
          rows.push(spanRow(field, null, { from: null, to: listId, title: names.get(listId) }, at))
        }
      }
    }
    return rows
  }

  /** A record by id. Throws when none matches. */
  const get = async (id: number): Promise<FullRecord> => {
    const { record } = await query(fetchRecordById, { id })
    if (!record) throw new Error(`Record not found: #${id}`)
    return record
  }

  /** A record by its (type, slug). Throws when none matches. */
  const getBySlug = async (type: string, slug: string): Promise<FullRecord> => {
    const { records } = await query(fetchRecordBySlug, { type, slug })
    const record = records[0]
    if (!record) throw new Error(`Record not found: ${type}/${slug}`)
    return record
  }

  const list = async (ids: number[]): Promise<ListedRecord[]> => {
    if (!ids.length) return []
    return (await query(fetchRecordsByIds, { ids })).records
  }

  /** Records whose name matches `q` (an ilike pattern). With a `filter` (and
   *  a `type` for it to run on), only that filter's matches: a filtered link
   *  field's picker. */
  /** The records (people by default) holding each email address, keyed by the
   *  address lowercased. An address with no match is absent; one held by several
   *  records maps to the first found. Stored emails keep their case, and the
   *  match is exact, so each address is looked up both as given and lowercased. */
  const findByEmails = async (
    emails: readonly string[],
    opts: { type?: string } = {}
  ): Promise<Map<string, EmailMatch>> => {
    const trimmed = emails.map((e) => e.trim()).filter(Boolean)
    const wanted = [...new Set(trimmed.flatMap((e) => [e.toLowerCase(), e]))]
    const found = new Map<string, EmailMatch>()
    if (!wanted.length) return found
    const { record_contacts } = await query(fetchRecordsByEmails, {
      emails: wanted,
      type: opts.type ?? "person",
    })
    for (const { value, record } of record_contacts) {
      const key = value.toLowerCase()
      if (record && !found.has(key)) found.set(key, record)
    }
    return found
  }

  const search = async (
    q: string,
    opts: { type?: string | null; limit?: number; filter?: SavedFilter | null } = {}
  ): Promise<SearchedRecord[]> => {
    if (opts.type != null && hasActiveConditions(opts.filter)) {
      const { filter } = opts
      return (
        await query(searchFilteredRecords, {
          recordType: opts.type,
          filter,
          query: q,
          limit: opts.limit,
        })
      ).records
    }
    const data =
      opts.type == null
        ? await query(searchAnyRecords, { query: q, limit: opts.limit })
        : await query(searchRecords, { query: q, type: opts.type, limit: opts.limit })
    return data.records
  }

  // A filtered link field takes only targets its filter matches: every write
  // adding one comes through here (the picker, MCP, imports), so the filter
  // holds wherever links are written. Removing a link, or keeping one that is
  // already there, is never refused.
  const assertLinkTargetsInFilter = async (
    relations: RecordWritePlan["relations"],
    verb: string
  ): Promise<void> => {
    for (const { field, add } of relations) {
      if (field.type !== "link" || !add.length || !field.recordType) continue
      if (!hasActiveConditions(field.filter)) continue
      const label = field.label || field.key
      const ids = [...new Set(add.map((e) => Number(e.id)))]
      let matched: Set<number>
      try {
        const { records } = await query(fetchFilterMatchIds, {
          recordType: field.recordType,
          filter: field.filter,
          ids,
        })
        matched = new Set(records.map((r) => r.id))
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        throw new Error(
          `records.${verb}: ${label}'s filter can't run, so it takes no new links: ${msg}`,
          { cause: e }
        )
      }
      const refused = add.filter((e) => !matched.has(Number(e.id)))
      if (refused.length) {
        const names = refused.map((e) =>
          typeof e.name === "string" && e.name ? e.name : `#${e.id}`
        )
        throw new Error(
          `records.${verb}: ${label} only takes ${field.recordType} records matching its filter; ` +
            `${names.join(", ")} ${refused.length === 1 ? "doesn't" : "don't"}`
        )
      }
    }
  }

  // records.slug is varchar(64). A longer slug is refused here rather than cut:
  // a cut slug is not the one the caller asked for, and Postgres would refuse
  // it anyway, with a message that names neither the record nor the length.
  const assertSlugFits = (slug: string, verb: string) => {
    if (slug.length > SLUG_MAX_LENGTH) {
      throw new Error(
        `records.${verb}: slug is too long (${slug.length} chars); the maximum is ${SLUG_MAX_LENGTH}`
      )
    }
  }

  // A record's name and slug can't be blank: a nameless record is unfindable in
  // every list and picker, and an empty slug both dodges the slug trigger and
  // breaks the record's URL. Checked wherever a write names either.
  const assertNameAndSlug = (changes: Record<string, unknown>, verb: string) => {
    for (const key of ["name", "slug"] as const) {
      if (!(key in changes) || changes[key] === undefined) continue
      const value = changes[key]
      if (typeof value !== "string" || !value.trim()) {
        throw new Error(`records.${verb}: ${key} cannot be empty`)
      }
      if (key === "slug") assertSlugFits(value.trim(), verb)
    }
  }

  /**
   * Refuse giving `children` the parent `parentId` when the parent is missing or
   * archived, when it would make a record its own ancestor, or when the parent
   * declares a `child_type` (a series) and a child is another type. Every write
   * that sets `parent_id` runs it, so no interface can skip it.
   */
  const assertParent = async (
    parentId: unknown,
    children: Array<{ id: number | null; type: string }>,
    verb: string
  ): Promise<void> => {
    if (typeof parentId !== "number" || !Number.isInteger(parentId)) {
      throw new Error(
        `records.${verb}: parent_id must be a record id or null, not ${JSON.stringify(parentId)}`
      )
    }
    const fetchOne = async (id: number) => (await query(fetchRecordLeanById, { id })).record
    const parent = await fetchOne(parentId)
    if (!parent || parent.archived_at) {
      throw new Error(`records.${verb}: parent #${parentId} does not exist or is archived`)
    }
    const data = (parent.data ?? {}) as Record<string, unknown>
    const childType =
      typeof data.child_type === "string" && data.child_type ? data.child_type : null
    const wrong = childType ? children.find((child) => child.type !== childType) : undefined
    if (childType && wrong) {
      throw new Error(
        `records.${verb}: "${parent.name}" (#${parent.id}) holds ${childType} records, ` +
          `so ${wrong.type} records can't be its children`
      )
    }
    const childIds = new Set(
      children.map((child) => child.id).filter((id): id is number => id != null)
    )
    let current: typeof parent | null | undefined = parent
    for (let depth = 0; current; depth++) {
      if (childIds.has(current.id)) {
        throw new Error(
          `records.${verb}: #${current.id} can't be a child of #${parentId} — ` +
            `#${parentId} is #${current.id} itself or one of its descendants`
        )
      }
      if (current.parent_id == null) return
      if (depth >= MAX_PARENT_DEPTH) {
        throw new Error(
          `records.${verb}: #${parentId}'s parent chain is deeper than ${MAX_PARENT_DEPTH}`
        )
      }
      current = await fetchOne(current.parent_id)
    }
  }

  // The insert object for one new record, planned like any write: relation-typed
  // keys in `data` leave it and become nested rows. Shared by create/createMany.
  const insertObject = async (
    input: CreateRecordInput,
    verb: string
  ): Promise<Record<string, unknown>> => {
    // records.type is NOT NULL, and an empty type resolves no schema — enrichRecord
    // reads it as "no type" — so without this the insert fails in Postgres instead.
    if (typeof input.type !== "string" || !input.type.trim()) {
      throw new Error(`records.${verb}: type cannot be empty`)
    }
    assertNameAndSlug({ name: input.name }, verb)
    if (input.parent_id != null)
      await assertParent(input.parent_id, [{ id: null, type: input.type }], verb)
    // The planner needs a field list and each relation's CURRENT set. For a new
    // record the field list comes from the schema alone (the input's `schema_id`,
    // else the type's default), and every current set is empty — so a blank
    // stand-in supplies both, with the relation arrays spelled out as [] (truthful
    // for a new record; the planner refuses to diff an ABSENT relation, since on a
    // fetched record that means "not selected", not "empty").
    const blank: RecordItem = {
      id: 0,
      type: input.type,
      ...(input.schema_id != null ? { schema_id: input.schema_id } : {}),
      data: {},
      links: [],
      taggings: [],
      attachments: [],
      contacts: [],
      times: [],
      sources: [],
    }
    const { fields } = enrichRecord(blank)
    const plan = planRecordWrite(fields, blank, input.data ?? {})
    await assertLinkTargetsInFilter(plan.relations, verb)
    await resolveAssetTargets(query, plan.relations)

    // Anything in `plan._set` besides `data` means a key inside `input.data`
    // resolved to a fixed column (e.g. `owner_id`) rather than a `data` field —
    // `object` below only reads `plan._set.data`, so a caller who nested a fixed
    // column there would otherwise have it silently dropped. `CreateRecordInput`
    // has its own top-level param for every fixed column; nest it there instead.
    const strayKeys = Object.keys(plan._set).filter((key) => key !== "data")
    if (strayKeys.length) {
      throw new Error(
        `records.${verb}: ${JSON.stringify(strayKeys)} belongs on CreateRecordInput directly, ` +
          `not nested inside \`data\` — nesting it there would be silently dropped.`
      )
    }

    // Relation-typed keys leave `data` and become nested rows. Hasura fills each
    // child's `record_id` from the parent inside one transaction, so this needs
    // no id up front and a failing child rolls the whole create back — which is
    // also why create has no per-relation row-count check: partial insert is
    // impossible here (live-verified: a child FK violation left no parent row),
    // unlike update's separate-mutation path that applyRelationOps count-checks.
    // The slug key is OMITTED unless the caller supplied one, never sent as
    // null: `records.slug` is NOT NULL, and Hasura's column parser rejects an
    // explicit null for a NOT NULL column ("unexpected null value for type
    // 'String'", code validation-failed) before the INSERT — so
    // set_default_record_slug never gets to fill it. Non-empty slugs are stored
    // TRIMMED: " acme " would skip the trigger and then miss every slug-keyed
    // lookup, and "" would satisfy NOT NULL and mint a ''-slug row.
    const explicitSlug = input.slug?.trim()
    if (explicitSlug) assertSlugFits(explicitSlug, verb)
    const data = (plan._set.data as Record<string, unknown> | undefined) ?? {}
    const relationInserts = buildRelationInserts(plan.relations)
    const spanRows = await openingSpans(fields, input, data)
    if (spanRows.length) {
      relationInserts.times = { data: [...(relationInserts.times?.data ?? []), ...spanRows] }
    }
    const object = {
      type: input.type,
      name: input.name.trim(),
      ...(explicitSlug ? { slug: explicitSlug } : {}),
      // Trimmed, and blank stored as null: form callers pass the raw
      // textarea value, and "" is not a description.
      description: input.description?.trim() || null,
      parent_id: input.parent_id ?? null,
      schema_id: input.schema_id ?? null,
      visibility: input.visibility ?? "shared",
      data,
      ...relationInserts,
      ...(input.listIds?.length
        ? { listings: { data: input.listIds.map((list_id) => ({ list_id })) } }
        : {}),
    }
    return object
  }

  const create = async (input: CreateRecordInput): Promise<CreatedRecord> => {
    const object = await insertObject(input, "create")

    const created = (await mutate(createRecordNested, { object })).insert_records_one
    if (!created) throw new Error("Record creation returned no row")
    return created
  }

  /**
   * Create many records — each planned exactly like `create`, nested rows and
   * list memberships included — in ONE insert, so the batch is all-or-nothing.
   * Callers chunk large imports: one bad row costs its whole batch.
   */
  const createMany = async (inputs: CreateRecordInput[]): Promise<CreatedRecord[]> => {
    if (!inputs.length) return []
    const objects = await Promise.all(inputs.map((input) => insertObject(input, "createMany")))
    const created = (await mutate(createRecordsNested, { objects })).insert_records?.returning
    if (!created) throw new Error(`createMany: creating ${inputs.length} records returned no rows`)
    // Hasura reports rows it inserted but could not select back as simply
    // absent, so a short result is a partial success unless it is named.
    if (created.length !== inputs.length) {
      throw new Error(
        `createMany: created ${inputs.length} records but ${created.length} came back — ` +
          `some may exist but not be visible to you`
      )
    }
    return created
  }

  // Returns `{ record, written }`, not the record alone. `written` holds, keyed
  // by field key, a row for every relation target this call added: the rows it
  // INSERTED, straight from each insert's `returning`, then — because adding
  // something that already exists returns the thing that already exists — the
  // field's current row for each `add` target it already held. So a caller
  // names the row for what it added without re-reading and searching for it by
  // value, which is how #1294 shipped a false "not present after the write" for
  // a committed row.
  //
  // Overloaded rather than a single union return: the ergonomic `record(id)`
  // handle (services/handle.ts) always wants the full record and never passes
  // `opts`, so its callers should not have to narrow a union they can't
  // produce. Only a caller that explicitly opts out of the trailing read gets
  // the narrower `record` type back.
  async function update(
    id: number,
    changes: Record<string, unknown>
  ): Promise<{ record: FullRecord; written: WrittenRows }>
  async function update(
    id: number,
    changes: Record<string, unknown>,
    opts: { read: false }
  ): Promise<{ record: RecordRow | { id: number }; written: WrittenRows }>
  async function update(
    id: number,
    changes: Record<string, unknown>,
    opts?: { read?: boolean }
  ): Promise<{ record: FullRecord | RecordRow | { id: number }; written: WrittenRows }> {
    assertNameAndSlug(changes, "update")
    // Read weight matches the patch: a column/data-only patch plans against a
    // lean read (columns incl. data, no relation rows); only a patch touching a
    // relation-typed field pays the relation-hydrating fetch it actually needs.
    // The planner's hydration guard backstops any misclassification.
    const leanData = await query(fetchRecordLeanById, { id })
    const lean = leanData.record
    if (!lean) throw new Error(`Record not found: #${id}`)
    if (changes.parent_id != null)
      await assertParent(changes.parent_id, [{ id, type: lean.type }], "update")
    const { fields } = enrichRecord(lean)
    const byKey = new Map(fields.map((f) => [f.key, f]))
    const touchesRelations = Object.entries(changes).some(([key, value]) => {
      if (value === undefined) return false
      const field = byKey.get(key)
      return !!field && isRelationField(field)
    })
    const full = touchesRelations ? await get(id) : null
    const plan = planRecordWrite(fields, full ?? lean, changes)
    await assertLinkTargetsInFilter(plan.relations, "update")
    await resolveAssetTargets(query, plan.relations)
    // Build every relation row now, before the column write: a bad input
    // refuses the whole update instead of failing after the columns landed.
    const relationOps = prepareRelationOps(id, plan.relations)
    let columnRow: RecordRow | null = null
    if (plan.hasChanges) {
      // update_records_by_pk returns null — no error — when the row doesn't
      // match the mutation's permission filter, or was deleted between the read
      // above and this write. The raw-mutation path this replaced explicitly
      // checked for that; discarding the result here would report success for a
      // write that changed nothing.
      const res = await mutate(updateRecordById, { id, _set: plan._set })
      const updated = res.update_records_by_pk
      if (!updated) {
        throw new Error(
          `update: record ${id} not found, or not permitted to write — the column ` +
            `update matched no row`
        )
      }
      columnRow = updated
    }
    // Column and relation writes are two separate mutations, not one transaction
    // — the same Hasura limit applyRelationOps documents for its own two steps.
    // If the column write above already landed and this one then fails, the
    // record is left with its NEW column values and OLD (or partially-changed)
    // relations, and there is no rollback. Naming that here, at the one call
    // site that knows both facts, turns "relation write failed" into something
    // a caller can actually act on instead of guessing at the record's state.
    let written = noRowsWritten()
    if (relationOps.length) {
      try {
        written = await applyRelationOps(mutate, relationOps)
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        throw new Error(
          `update: record ${id}'s column changes ${plan.hasChanges ? "already committed" : "were a no-op"} ` +
            `— the relation write that followed failed and was not rolled back: ${msg}`,
          { cause: e }
        )
      }
    }
    if (columnRow) {
      try {
        await stampChanges(id, fields, lean, changes, columnRow.updated_at)
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        throw new Error(
          `update: record ${id}'s changes committed, but stamping their spans failed: ${msg}`,
          { cause: e }
        )
      }
    }
    for (const { field, existing } of plan.relations) {
      if (!existing.length) continue
      // A relation op comes only from a relation field, which made the read
      // above the full one.
      if (!full)
        throw new Error(`update: record ${id} has relation changes but no relation rows were read`)
      const relation = relationOf(field)
      const ids = new Set(existing.map((row) => row.id))
      addWritten(
        written,
        relation,
        field.key,
        currentRows(full, relation).filter((row) => ids.has(row.id))
      )
    }
    // get(id) re-fetches the full record — every relation array, every
    // sub-selection fetchRecordById carries — even for a caller that only
    // wants to know the write succeeded. `read: false` skips it and hands back
    // what the write itself returned: the column update's row when there was
    // one, else just `{ id }`.
    if (opts?.read === false) return { record: columnRow ?? { id }, written }
    return { record: await get(id), written }
  }

  /**
   * Link a record to a schema, or unlink it with `null`. Only the column
   * changes: `data` stays as it is, so no field of either schema is read or
   * planned. That is why this does not go through `update`, which resolves the
   * record's CURRENT schema first and refuses when the catalog cannot — the
   * case unlinking exists to recover from. Returns the written row.
   */
  const setSchema = async (id: number, schemaId: number | null): Promise<RecordRow> => {
    const updated = (await mutate(updateRecordById, { id, _set: { schema_id: schemaId } }))
      .update_records_by_pk
    if (!updated) throw new Error(`Record not found, or not permitted to write: #${id}`)
    return updated
  }

  /**
   * Make `parentId` the parent of every record in `childIds`, or detach them
   * with `null`, in one write. The parent must exist and be live, can't be one
   * of the children or below them, and when it declares a `child_type` (a
   * series) every child must be that type; any refusal writes nothing.
   */
  const setParent = (childIds: number[], parentId: number | null): Promise<UpdateManyResult> =>
    updateMany(childIds, { parent_id: parentId })

  /**
   * Apply the same changes to many records in ONE write. Every key is checked
   * against each record's own fields first (an undeclared key refuses the whole
   * batch before anything is written). Columns go through `_set`; `data` keys
   * through `_append`, which Postgres merges into each row's current data, so
   * nothing read earlier is written back over a newer value.
   *
   * Relation fields aren't accepted: a relation's value is a set of rows per
   * record, which one statement can't reconcile. Use `update` per record.
   */
  const updateMany = async (
    rawIds: number[],
    changes: Record<string, unknown>
  ): Promise<UpdateManyResult> => {
    const ids = [...new Set(rawIds)]
    if (!ids.length) return { updated: [], missingIds: [] }
    assertNameAndSlug(changes, "updateMany")

    const found = (await query(fetchRecordsLeanByIds, { ids })).records
    const foundIds = new Set(found.map((r) => r.id))
    const missingIds = ids.filter((id) => !foundIds.has(id))
    if (changes.parent_id != null && found.length) {
      await assertParent(
        changes.parent_id,
        found.map((r) => ({ id: r.id, type: r.type })),
        "updateMany"
      )
    }

    const fieldsById = new Map<number, AugerField[]>()
    for (const record of found) {
      const { fields } = enrichRecord(record)
      fieldsById.set(record.id, fields)
      for (const [key, value] of Object.entries(changes)) {
        if (value === undefined) continue
        const field = fields.find((f) => f.key === key)
        if (field && isRelationField(field)) throw new RelationFieldInBatchError(key)
      }
      planRecordWrite(fields, record, changes)
    }

    const _set: Record<string, unknown> = {}
    const append: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined) continue
      if (RECORD_FIXED_KEYS.includes(key)) _set[key] = value
      else if (!RECORD_COLUMNS.has(key)) append[key] = value
    }
    const ownerSet = ownerWrite(_set)
    if (ownerSet !== _set) {
      for (const key of Object.keys(_set)) delete _set[key]
      Object.assign(_set, ownerSet)
    }
    if (!found.length || (!Object.keys(_set).length && !Object.keys(append).length)) {
      return { updated: found, missingIds }
    }

    const res = await mutate(updateRecordsMany, {
      ids: found.map((r) => r.id),
      _set,
      _append: Object.keys(append).length ? { data: append } : {},
    })
    const result = res.update_records
    if (!result) throw new Error("updateMany: Hasura returned no update_records result")
    const written = new Set(result.returning.map((r) => r.id))
    const updatedAt = new Map(result.returning.map((r) => [r.id, r.updated_at]))
    for (const record of found) {
      const at = updatedAt.get(record.id)
      if (at) await stampChanges(record.id, fieldsById.get(record.id) ?? [], record, changes, at)
    }
    return {
      updated: result.returning,
      missingIds: [...missingIds, ...found.map((r) => r.id).filter((id) => !written.has(id))],
    }
  }

  // Records are DESTROYED, never plainly removed: records has no delete
  // permission for any role, and a plain DELETE trips the audit triggers (they
  // log the delete against the row being removed and fail its FK).
  // destroy_record suspends them for the statement. Site-admin only: run these
  // through an Auger whose transport carries that role.

  /** Destroy one record — its relation rows go with it. Throws when no record
   *  matched the id. */
  const destroy = async (id: number): Promise<{ id: number }> => {
    const deleted = (await mutate(destroyRecord, { id })).destroy_record?.[0]
    if (!deleted) throw new Error(`Record ${id} not found for delete`)
    return deleted
  }

  /**
   * Destroy many records. destroy_record takes one id, so each is its own call
   * and its own transaction — run together, each reported on its own: a
   * destroyed record stays destroyed whatever happens to the others.
   */
  const destroyMany = async (rawIds: number[]): Promise<DestroyManyResult> => {
    const ids = [...new Set(rawIds)]
    const result: DestroyManyResult = { destroyedIds: [], missingIds: [], failures: [] }
    const settled = await Promise.allSettled(ids.map((id) => mutate(destroyRecord, { id })))
    settled.forEach((outcome, i) => {
      const id = ids[i]!
      if (outcome.status === "rejected") {
        const reason = outcome.reason
        result.failures.push({
          id,
          message: reason instanceof Error ? reason.message : String(reason),
        })
        return
      }
      const rows = outcome.value.destroy_record
      if (rows?.length) result.destroyedIds.push(id)
      else result.missingIds.push(id)
    })
    return result
  }

  /**
   * Fold `sourceId` into `targetId`: the database moves the source's links,
   * contacts, listings and other rows onto the target and deletes the source,
   * all in one transaction. Both records must be of `type`. Returns the target.
   */
  const merge = async (sourceId: number, targetId: number, type: string): Promise<RecordRow> => {
    if (sourceId === targetId) throw new Error(`merge: cannot merge record ${sourceId} into itself`)
    const merged = (await mutate(mergeRecords, { sourceId, targetId, type })).merge_records?.[0]
    if (!merged) throw new Error(`merge: merging ${sourceId} into ${targetId} returned no record`)
    return merged
  }

  // Archived rows carry no relation selections (see the doc comment on the
  // conditional mutations in gql/records.js) — RecordRow, the columns alone. A
  // caller wanting the relations can get(id) after.
  const archiveMany = async (rawIds: number[]): Promise<ArchiveManyResult> => {
    if (!rawIds.length) return { archived: [], alreadyArchivedIds: [], missingIds: [] }
    // Dedupe up front — a repeated id in the input would otherwise appear
    // twice in `missingIds` (the one output that isn't already deduped by
    // going through a Set, unlike `archived`/`alreadyArchivedIds`).
    const ids = [...new Set(rawIds)]

    // Precheck BEFORE the mutation, not in parallel with it (sequenced, not
    // raced) — a strictly earlier snapshot can't observe a write this call
    // hasn't issued yet, which is what made the original parallel version
    // misread a real id as missing purely from dispatch timing.
    //
    // The race that sequencing does NOT remove: a THIRD PARTY archiving (or
    // deleting) the row between this precheck's read and this mutation. That
    // row was live when the precheck saw it, so it's real — but the
    // conditional mutation's WHERE (archived_at IS NULL) then matches
    // nothing for it, and classifying by the PRECHECK's archived_at value
    // (which was null) would misfile it as `missingIds`, indistinguishable
    // from an id that never existed. Classify by PRESENCE in the precheck
    // instead: anything the precheck saw at all, and this call didn't just
    // freshly archive, is "already in the target state" from the caller's
    // perspective — whether that's because it was already archived, or
    // because someone else archived it in the interim makes no difference
    // to what the caller asked for.
    const pre = await query(fetchRecordsArchivedState, { ids })
    const res = await mutate(archiveRecordsConditional, { ids })
    const updated = res.update_records
    if (!updated) throw new Error("archiveMany: Hasura returned no update_records result")

    const archived = updated.returning
    const freshlyArchivedIds = new Set(archived.map((r) => r.id))
    const seen = new Set(pre.records.map((r) => r.id))
    const alreadyArchivedIds = pre.records
      .filter((r) => !freshlyArchivedIds.has(r.id))
      .map((r) => r.id)
    return { archived, alreadyArchivedIds, missingIds: ids.filter((id) => !seen.has(id)) }
  }

  const unarchiveMany = async (rawIds: number[]): Promise<UnarchiveManyResult> => {
    if (!rawIds.length) return { unarchived: [], alreadyUnarchivedIds: [], missingIds: [] }
    const ids = [...new Set(rawIds)]

    // See the same comment in archiveMany: precheck strictly before the
    // mutation (sequenced, not raced), and classify by PRESENCE in the
    // precheck rather than by its archived_at value — a third party
    // unarchiving the row between this precheck and this mutation is real
    // and was seen live, just not by this call.
    const pre = await query(fetchRecordsArchivedState, { ids })
    const res = await mutate(unarchiveRecordsConditional, { ids })
    const updated = res.update_records
    if (!updated) throw new Error("unarchiveMany: Hasura returned no update_records result")

    const unarchived = updated.returning
    const freshlyUnarchivedIds = new Set(unarchived.map((r) => r.id))
    const seen = new Set(pre.records.map((r) => r.id))
    const alreadyUnarchivedIds = pre.records
      .filter((r) => !freshlyUnarchivedIds.has(r.id))
      .map((r) => r.id)
    return { unarchived, alreadyUnarchivedIds, missingIds: ids.filter((id) => !seen.has(id)) }
  }

  // Single-record archive/unarchive is archiveMany/unarchiveMany of one, not a
  // separate mutation — one conditional-update shape to keep correct, and an
  // already-archived record is idempotent success (the current row, unchanged)
  // rather than an error, matching update()'s own no-changes-is-success rule.
  const archive = async (id: number): Promise<RecordRow> => {
    const { archived, missingIds } = await archiveMany([id])
    if (missingIds.length) throw new Error(`Record ${id} not found for archive`)
    // archiveMany's missingIds is exactly `ids` minus every id it found in
    // `archived` or `alreadyArchivedIds` — for a single id, not missing means
    // one of those two holds. archived.length rules out the first; the only
    // remaining possibility is that the row is already archived, so get
    // returns its current, unchanged row.
    if (archived.length) return archived[0]!
    const current = await get(id)
    // The "already archived" inference holds only while records' select and
    // update permission filters stay identical (they are today). If they ever
    // diverge, this row can come back LIVE — and returning it as success
    // would report an archive that did not happen. One line keeps the
    // inference honest instead of trusted.
    if (current.archived_at == null) {
      throw new Error(
        `Record ${id} is still live after a conditional archive that matched ` +
          `nothing — the select filter shows a row the update filter won't touch. ` +
          `The record was NOT archived.`
      )
    }
    return current
  }

  const unarchive = async (id: number): Promise<RecordRow> => {
    const { unarchived, missingIds } = await unarchiveMany([id])
    if (missingIds.length) throw new Error(`Record ${id} not found for unarchive`)
    if (unarchived.length) return unarchived[0]!
    const current = await get(id)
    // The mirror of archive()'s guard: "already unarchived" is an inference
    // from not-missing-and-not-freshly-unarchived, valid only while records'
    // select and update permission filters stay identical. If they diverge,
    // this row can come back STILL ARCHIVED — and returning it as success
    // would report an unarchive that did not happen.
    if (current.archived_at != null) {
      throw new Error(
        `Record ${id} is still archived after a conditional unarchive that matched ` +
          `nothing — the select filter shows a row the update filter won't touch. ` +
          `The record was NOT unarchived.`
      )
    }
    return current
  }

  return {
    get,
    getBySlug,
    list,
    search,
    findByEmails,
    create,
    createMany,
    update,
    updateMany,
    setSchema,
    setParent,
    destroy,
    destroyMany,
    merge,
    archive,
    unarchive,
    archiveMany,
    unarchiveMany,
  }
}
